import React, { useState, useEffect } from 'react';
import { LayoutGrid, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { MODULES } from '../../modules/registry';

/**
 * ModuleVisibilityCard — Admin-Schalter „App für Benutzer sichtbar" (Taras 30.07.).
 *
 * Ebene 2 der Modul-Sichtbarkeit (zwischen Lizenz und Berechtigungsprofil): Kunde kauft
 * z. B. 4 Apps, will aber nur 3 generell sichtbar haben — die ausgeblendete sieht nur der
 * Admin. Persistenz: general_settings 'module_visibility' über den bestehenden
 * Admin-Settings-Endpunkt; nur explizites false blendet aus.
 */
const ModuleVisibilityCard = ({ user, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [moduleVisibility, setModuleVisibility] = useState({});
  const [licenseInfo, setLicenseInfo] = useState(null); // { licensedModules: [], mode } | null
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [settingsRes, licenseRes] = await Promise.all([
          fetch('/api/admin/settings', { headers: { Authorization: `Bearer ${user.token}` } }),
          fetch('/api/license', { headers: { Authorization: `Bearer ${user.token}` } }),
        ]);
        if (!settingsRes.ok || !licenseRes.ok) throw new Error('load failed');
        const settings = await settingsRes.json();
        const license = await licenseRes.json();
        if (!alive) return;
        try {
          const raw = settings?.module_visibility;
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) setModuleVisibility(parsed);
        } catch { /* ungültiges JSON -> alles sichtbar */ }
        setLicenseInfo({ licensedModules: license.licensedModules || [], mode: license.mode || 'grace' });
        setLoadState('ready');
      } catch (err) {
        console.error('ModuleVisibilityCard load error:', err);
        if (alive) setLoadState('error');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  const toggle = async (moduleKey) => {
    const next = { ...moduleVisibility, [moduleKey]: moduleVisibility[moduleKey] === false };
    const previous = moduleVisibility;
    setModuleVisibility(next);
    setSaving(true);
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ module_visibility: JSON.stringify(next) }),
      });
      if (!res.ok) throw new Error('save failed');
      showSuccess?.(t('admin.license.visibilitySaved'));
    } catch (err) {
      console.error('module_visibility save error:', err);
      setModuleVisibility(previous); // ehrlich zurückrollen statt falschen Zustand anzeigen
      showError?.(t('admin.license.visibilityError'));
    } finally {
      setSaving(false);
    }
  };

  const grace = !licenseInfo || licenseInfo.mode === 'grace';

  return (
    <div className="relative group self-start lg:col-span-2">
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
        : 'glass-light-card'
      } rounded-3xl`}></div>
      <div className="relative p-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
            <LayoutGrid className={`w-5 h-5 ${isDarkMode ? 'text-blue-300' : 'text-brand'}`} />
          </div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('admin.license.visibilityTitle')}
          </h3>
        </div>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          {t('admin.license.visibilityDesc')}
        </p>

        {loadState === 'loading' && (
          <p className={`text-sm py-4 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
            {t('admin.license.visibilityLoading')}
          </p>
        )}
        {loadState === 'error' && (
          <p className="text-sm py-4 text-red-500 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {t('admin.license.visibilityLoadError')}
          </p>
        )}

        {loadState === 'ready' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {MODULES.filter((m) => m.tile_in_homepage && m.licenseModuleKey).map((m) => {
              const key = m.licenseModuleKey;
              const licensed = grace || (Array.isArray(licenseInfo.licensedModules) && licenseInfo.licensedModules.includes(key));
              const visible = moduleVisibility[key] !== false;
              return (
                <div key={m.id} className={`flex items-center justify-between px-4 py-3 rounded-2xl border ${
                  isDarkMode ? 'border-white/10 bg-gray-800/30' : 'border-slate-200 bg-white'
                }`}>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
                      {t(m.tile.titleKey)}
                    </p>
                    {/* Grace = "unbekannt", bewusst amber statt grün */}
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      grace
                        ? (isDarkMode ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700')
                        : licensed
                          ? (isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
                          : (isDarkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600')
                    }`}>
                      {grace ? t('admin.license.stGrace') : licensed ? t('admin.license.stLicensed') : t('admin.license.stNotLicensed')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                      {t('admin.license.visibilityVisible')}
                    </span>
                    <button
                      role="switch"
                      aria-checked={visible}
                      disabled={!licensed || saving}
                      onClick={() => toggle(key)}
                      className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 disabled:opacity-40 ${
                        visible ? 'bg-brand' : (isDarkMode ? 'bg-white/20' : 'bg-slate-300')
                      }`}
                      title={licensed ? '' : t('admin.license.stNotLicensed')}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${visible ? 'left-[18px]' : 'left-0.5'}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className={`mt-3 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('admin.license.visibilityHint')}
        </p>
      </div>
    </div>
  );
};

export default ModuleVisibilityCard;
